import {FC, MouseEvent, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Link} from 'react-router-dom';
import {BadgeOutlined, ContentCopyOutlined} from '@mui/icons-material';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import copy from 'copy-to-clipboard';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {MissingBexioPill} from 'src/components/common/MissingBexioPill';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import {
    factLinkSx,
    iconBtnSx,
    idValueSx,
    menuBtnSx,
    menuBtnTextSx,
    popoverSx,
    titleLinkSx,
} from './EmployeeItemCard.sx';
import dayjs from 'dayjs';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {employeeApi} from 'src/api';
import {AppDispatch} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {useBusinessId, useAppDispatch} from 'src/app/hooks';
import {buildEmployeeLink} from 'src/components/common/CompanyEmployeesTable/CompanyEmployeesTable.tableConfig';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {EmployeeDeleteRequest, EmployeeDTO} from 'src/types/employee';
import {EmployeeStatus} from '../EmployeeStatus/EmployeeStatus';
import {PaymentModeStatus} from '../PaymentModeStatus';
import {EditEmployee} from '../EditEmployee';
import {RegistrationStatusEnum, UserTypeEnum} from 'src/enums';
import {ResendRegistration} from 'src/components/users/ResendRegistration';

type DeleteEmployeeMutationTrigger = ReturnType<
    typeof employeeApi.useDeleteEmployeeByIdMutation
>[0];

const handleDeleteEmployee =
    (trigger: DeleteEmployeeMutationTrigger, input: EmployeeDeleteRequest, dispatch: AppDispatch) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));

                await trigger(input)
                    .unwrap()
                    .finally(() => {
                        dispatch(preloaderChangeStatus(false));
                    });
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const EmployeeItemCard: FC<EmployeeDTO> = (employee) => {
    // The menu exists only to offer this action, so the permission decides whether it appears.
    // Where a flag is also passed in, both must hold: the parent says whether the action belongs
    // on this screen, the permission says whether this person may perform it - and the prop
    // defaults to true, so on its own it fails open.
    const canDeleteEmployee = useCan('employees.delete');
    const {
        id,
        firstName,
        lastName,
        company,
        consumptionPaymentMode,
        user: {emailAddress, created, registrationStatus},
    } = employee;
    const businessId = useBusinessId();

    const intl = useIntl();
    const [triggerDeleteEmployee] = employeeApi.useDeleteEmployeeByIdMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(id), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    const displayName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || emailAddress || '';
    // The employee's own page, read in the tenant this list is being read in - the same destination
    // the row's name cell opens, built by the one helper that knows the path.
    const employeeLink = buildEmployeeLink(id, businessId, company?.id);
    // The warning the desktop row carries as a red name, which this card had no way to show: its
    // title is a plain link, not the name cell the grid renders. It applies only to an employee who
    // pays for their own consumption - the rule decides, the pill only shows.
    const showMissingBexio = useMissingBexio({
        hasBexio: !!employee.user?.bexioDataList?.length,
        userType: UserTypeEnum.EMPLOYEE,
        paymentMode: consumptionPaymentMode,
    });

    // The same two nodes for both widths. Split only so the sheet can put the destructive one below
    // a rule; the popover still renders them in the one column it always did.
    const primaryActions = (
        <>
            {registrationStatus === RegistrationStatusEnum.PENDING_HASH && (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                    <ResendRegistration
                        row={{...employee.user, chargePointId: '', canDeleteUser: false} as any}/>
                </Box>
            )}
            <EditEmployee row={employee}/>
        </>
    );

    const deleteAction = (
        <Button
            sx={menuBtnSx}
            onClick={handleDeleteEmployee(
                triggerDeleteEmployee,
                {
                    employeeId: id,
                    params: {
                        businessId,
                        companyId: company?.id,
                    },
                },
                dispatch
            )}
        >
            <Typography sx={menuBtnTextSx}>{intl.formatMessage({id: 'delete'})}</Typography>
        </Button>
    );

    // The menu the card's head carries. The sheet and the popover are portalled, so sitting in the
    // head's row costs the layout nothing.
    const menuAction = (
        <>
            {canDeleteEmployee && <MenuButton handleClick={handleClick}/>}

            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<BadgeOutlined/>}
                    title={`${firstName ?? ''} ${lastName ?? ''}`.trim() || emailAddress || ''}
                    subtitle={emailAddress}
                >
                    <ActionSheetIcons>{primaryActions}</ActionSheetIcons>
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        {primaryActions}
                        {deleteAction}
                    </Box>
                </Popover>
            )}
        </>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<BadgeOutlined/>}
                person
                title={
                    <Link to={employeeLink}>
                        <Typography
                            component="span"
                            sx={titleLinkSx}
                        >
                            {displayName}
                        </Typography>
                    </Link>
                }
                subtitle={emailAddress}
                action={menuAction}
            />
            <Box sx={phonePillsRowSx}>
                <EmployeeStatus label={registrationStatus as RegistrationStatusEnum}/>
                <PaymentModeStatus label={consumptionPaymentMode}/>
                {showMissingBexio && <MissingBexioPill/>}
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'userId'}),
                        value: (
                            <Box
                                component="span"
                                sx={idValueSx}
                            >
                                <Link to={employeeLink}>
                                    <Typography
                                        component="span"
                                        sx={factLinkSx}
                                    >
                                        {id}
                                    </Typography>
                                </Link>
                                <IconButton
                                    onClick={copyToClipboard}
                                    sx={iconBtnSx}
                                >
                                    <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                </IconButton>
                            </Box>
                        ),
                    },
                    {
                        label: intl.formatMessage({id: 'dateColumnHeader'}),
                        value: dayjs(created).format('DD/MM/YY'),
                    },
                ]}
            />
        </TableCard>
    );
};
